import app from 'flarum/forum/app';
import Page from 'flarum/components/Page';
import Button from 'flarum/common/components/Button';
import Link from 'flarum/common/components/Link';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';
import Placeholder from 'flarum/common/components/Placeholder';

import { fetchReviewTasks, resolveReviewTask } from '../utils/reviewTasks.mjs';

const reasonLabel = (reason) =>
  app.translator.trans(`yingtao-forum-homepage.forum.report.reason_${reason}`);

const formatDate = (value) => {
  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('zh-CN');
};

export default class ReviewTaskPage extends Page {
  oninit(vnode) {
    super.oninit(vnode);

    app.history.push('yingtaoReviewTasks');
    this.bodyClass = 'App--yingtaoReview';
    this.tasks = [];
    this.loading = true;
    this.error = null;
    this.busyTaskId = null;
    this.notes = {};
    this.focusedTaskId = null;
    this.load();
  }

  oncreate(vnode) {
    super.oncreate(vnode);
    this.scrollToRequestedTask();
  }

  onupdate() {
    this.scrollToRequestedTask();
  }

  scrollToRequestedTask() {
    if (this.loading || this.focusedTaskId !== null) return;

    const taskId = m.route.param('task');
    const target = taskId ? document.getElementById(`review-task-${taskId}`) : null;

    if (target) {
      this.focusedTaskId = String(taskId);
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  load() {
    this.loading = true;
    this.error = null;

    return fetchReviewTasks(app)
      .then((tasks) => {
        this.tasks = tasks;
      })
      .catch(() => {
        this.error = app.translator.trans('yingtao-forum-homepage.forum.review.load_error');
      })
      .finally(() => {
        this.loading = false;
        m.redraw();
      });
  }

  resolve(task, action) {
    if (this.busyTaskId !== null) return;

    if (action === 'delete' && !window.confirm(app.translator.trans('yingtao-forum-homepage.forum.review.confirm_delete'))) {
      return;
    }

    this.busyTaskId = task.id;
    this.error = null;

    resolveReviewTask(app, task.id, action, this.notes[task.id] || '')
      .then((result) => {
        task.status = result.status;
        task.resolutionNote = this.notes[task.id] || null;
      })
      .catch(() => {
        this.error = app.translator.trans('yingtao-forum-homepage.forum.review.resolve_error');
      })
      .finally(() => {
        this.busyTaskId = null;
        m.redraw();
      });
  }

  view() {
    const translation = 'yingtao-forum-homepage.forum.review';

    return (
      <div className="YingtaoReviewPage">
        <div className="container">
          <header className="YingtaoReviewPage-header">
            <div>
              <p className="YingtaoReviewPage-eyebrow">Cherrymaker club</p>
              <h1>{app.translator.trans(`${translation}.title`)}</h1>
            </div>
            <Button icon="fas fa-sync-alt" disabled={this.loading || this.busyTaskId !== null} onclick={() => this.load()}>
              {app.translator.trans(`${translation}.refresh`)}
            </Button>
          </header>

          {this.error ? <div className="Alert Alert--error YingtaoReviewPage-error" role="alert">{this.error}</div> : null}
          {this.loading ? <LoadingIndicator /> : this.taskList(translation)}
        </div>
      </div>
    );
  }

  taskList(translation) {
    if (!this.tasks.length) {
      return <Placeholder text={app.translator.trans(`${translation}.empty`)} />;
    }

    return <div className="YingtaoReviewList">{this.tasks.map((task) => this.task(task, translation))}</div>;
  }

  task(task, translation) {
    const busy = this.busyTaskId === task.id;
    const requestedTaskId = m.route.param('task');
    const isRequested = requestedTaskId && String(task.id) === String(requestedTaskId);

    return (
      <article
        className={`YingtaoReviewTask YingtaoReviewTask--${task.status}${isRequested ? ' YingtaoReviewTask--requested' : ''}`}
        key={task.id}
        id={`review-task-${task.id}`}
      >
        <header className="YingtaoReviewTask-header">
          <div className="YingtaoReviewTask-title">
            <span className={`YingtaoReviewStatus YingtaoReviewStatus--${task.status}`}>
              {app.translator.trans(`${translation}.status_${task.status}`)}
            </span>
            <h2>
              <Link href={app.route('discussion', { id: task.discussion.id })}>{task.discussion.title}</Link>
            </h2>
          </div>
          <dl className="YingtaoReviewTask-meta">
            <div><dt>{app.translator.trans(`${translation}.author`)}</dt><dd>{task.author.username || '-'}</dd></div>
            <div><dt>{app.translator.trans(`${translation}.cycle`)}</dt><dd>{task.cycleNumber}</dd></div>
            <div><dt>{app.translator.trans(`${translation}.report_count`)}</dt><dd>{task.reportCount}</dd></div>
            <div><dt>{app.translator.trans(`${translation}.created_at`)}</dt><dd>{formatDate(task.createdAt)}</dd></div>
          </dl>
        </header>

        <section className="YingtaoReviewTask-content">
          <h3>{app.translator.trans(`${translation}.content`)}</h3>
          <p>{task.post.content || '-'}</p>
        </section>

        <section className="YingtaoReviewTask-reports">
          <h3>{app.translator.trans(`${translation}.reports`)}</h3>
          <ul>
            {(task.reports || []).map((report, index) => (
              <li key={`${task.id}-${index}`}>
                <div>
                  <strong>{report.reporter.username || '-'}</strong>
                  <span>{reasonLabel(report.reason)}</span>
                  <time>{formatDate(report.createdAt)}</time>
                </div>
                {report.description ? <p>{report.description}</p> : null}
              </li>
            ))}
          </ul>
        </section>

        {task.status === 'pending' || task.status === 'deleted' ? (
          <footer className="YingtaoReviewTask-actions">
            <label>
              <span>{app.translator.trans(`${translation}.note_label`)}</span>
              <textarea
                className="FormControl"
                rows="2"
                maxlength="2000"
                value={this.notes[task.id] || ''}
                disabled={this.busyTaskId !== null}
                oninput={(event) => {
                  this.notes[task.id] = event.target.value;
                }}
                placeholder={app.translator.trans(`${translation}.note_placeholder`)}
              />
            </label>
            <div>
              {task.status === 'pending' ? (
                <>
                  <Button
                    className="Button Button--primary"
                    icon="fas fa-check"
                    loading={busy}
                    disabled={this.busyTaskId !== null}
                    onclick={() => this.resolve(task, 'restore')}
                  >
                    {app.translator.trans(`${translation}.restore`)}
                  </Button>
                  <Button
                    className="Button YingtaoReviewTask-delete"
                    icon="fas fa-trash-alt"
                    disabled={this.busyTaskId !== null}
                    onclick={() => this.resolve(task, 'delete')}
                  >
                    {app.translator.trans(`${translation}.delete`)}
                  </Button>
                </>
              ) : (
                <Button
                  className="Button Button--primary"
                  icon="fas fa-undo"
                  loading={busy}
                  disabled={this.busyTaskId !== null}
                  onclick={() => this.resolve(task, 'restore_deleted')}
                >
                  {app.translator.trans(`${translation}.restore_deleted`)}
                </Button>
              )}
            </div>
          </footer>
        ) : null}
      </article>
    );
  }
}
